import type { QuerySerializer } from 'openapi-fetch';
import createClient from 'openapi-fetch';

import type { paths } from '@/services/clients/editorApi/_generated/api';
import type { FilterObject } from '@/types/filters';

import { ApiTypes, getApiBase } from '../helpers';
import { createAuthHeaders } from './authTokenResolver';
import type { HttpServiceConfig } from './types';

// Improved type definitions
type QueryParams = Record<string, unknown>;

type OpenAPIClientConfig<TParams extends QueryParams> = {
  querySerializer?: QuerySerializer<TParams>;
} & HttpServiceConfig;

// Separated query serializer utility
function createQueryString(params: QueryParams): string {
  const validParams = Object.entries(params).filter(([, value]) => {
    return value !== undefined && value !== '';
  });

  const searchParams = new URLSearchParams();

  validParams.forEach(([key, value]) => {
    // Special handling for 'filters' parameter
    if (key === 'filters' && Array.isArray(value)) {
      // Check if this is an array of filter objects
      if (value.length > 0 && typeof value[0] === 'object' && 'filterKey' in value[0]) {
        const filterObjects = value as FilterObject[];
        // Convert filter objects to string format expected by API
        const filterString = filterObjects
          .map(filter =>
            `${filter.filterType}:${filter.filterKey}:${filter.filterValues.join('|')}`,
          )
          .join(',');

        if (filterString.length > 0) {
          searchParams.append(key, filterString);
        }
      } else {
        // It's already a string or other format, pass it through
        searchParams.append(key, String(value));
      }
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

export async function createOpenAPIClient<TParams extends QueryParams = QueryParams>(config: OpenAPIClientConfig<TParams>) {
  // Use local API only if explicitly enabled via env var
  // Otherwise, use the target-based API URL (works for both client and server)
  const useLocalApi = process.env.NEXT_PUBLIC_USE_LOCAL_API === 'true';
  const baseUrl = useLocalApi
    ? 'http://localhost:8787'
    : getApiBase(config.apiType ?? ApiTypes.EDITOR, config.target);

  const headers = await createAuthHeaders(config.authToken);

  return createClient<paths>({
    baseUrl,
    fetch,
    headers,
    querySerializer: createQueryString,
  });
}

export async function openAPIClient(httpRequestConfig: HttpServiceConfig) {
  return createOpenAPIClient(httpRequestConfig);
}
